import LocationOnIcon from "@mui/icons-material/LocationOn";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { debounce } from "@mui/material/utils";
import parse from "autosuggest-highlight/parse";
import { Dispatch, FunctionComponent, SetStateAction, useEffect, useMemo, useRef, useState } from "react";

// This key was created specifically for the demo in mui.com.
// You need to create a new one for your application.
const GOOGLE_MAPS_API_KEY = "AIzaSyDcQT7ikRgbzWU4Va4rYb75XvnWe5_Tqx0";

const loadScript = (src: string, position: HTMLElement | null, id: string) => {
  if (!position) {
    return;
  }
  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
};

const autocompleteService = { current: null };

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}
interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}
interface PlaceType {
  description: string;
  structured_formatting?: StructuredFormatting;
  place_id?: string;
}

interface Props {
  label?: string;
  onPlaceSelect?: (result: PlaceType | null) => void;
  defaultValue: string;
  // required: boolean | undefined;
  addressLengthError: boolean | undefined;
  setAddressLengthError: Dispatch<SetStateAction<boolean>>;
  addressError: boolean;
  setAddressError: Dispatch<SetStateAction<boolean>>;
}

export const GoogleMapsAutoComplete: FunctionComponent<Props> = (
  props: Props
): JSX.Element => {
  const { label, onPlaceSelect, defaultValue, addressLengthError, setAddressLengthError, addressError, setAddressError } = props;
  const [value, setValue] = useState<PlaceType | null>({
    description: defaultValue,
  });
  const [inputValue, setInputValue] = useState<string>("");
  const [options, setOptions] = useState<readonly PlaceType[]>([]);
  const loaded = useRef(false);

  useEffect(() => {
    // if (defaultValue !== value?.description) {
    // Update the value if the defaultValue changes
    setValue({ description: defaultValue });
    // }
  }, [defaultValue]);

  if (typeof window !== "undefined" && !loaded.current) {
    if (!document.querySelector("#google-maps")) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`,
        document.querySelector("head"),
        "google-maps"
      );
    }
    loaded.current = true;
  }

  const fetch = useMemo(
    () =>
      debounce(
        (
          request: { input: string },
          callback: (results?: readonly PlaceType[]) => void
        ) => {
          (autocompleteService.current as any).getPlacePredictions(
            request,
            callback
          );
        },
        400
      ),
    []
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (
        window as any
      ).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results?: readonly PlaceType[]) => {
      if (active) {
        let newOptions: readonly PlaceType[] = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <Autocomplete
      id="google-map-demo"
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      noOptionsText="No locations"

      onChange={(event: any, newValue: PlaceType | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        if (newValue) {
          if (newValue.description.length > 256) {
            setAddressLengthError(true)
            setAddressError(false)
          } else {
            setAddressLengthError(false)
            setAddressError(newValue.description.length === 0)
          }
        } else {
          setAddressLengthError(false);
          setAddressError(true);
        }
        if (onPlaceSelect) {
          onPlaceSelect(newValue);
        }
      }}

      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
        if (newInputValue.length > 256) {
          setAddressLengthError(true)
          setAddressError(false)
        } else {
          setAddressLengthError(false)
          setAddressError(newInputValue.length === 0)

        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? "Add a location"}
          fullWidth
          required
          error={addressLengthError || addressError}
          helperText={addressLengthError ? "Address is too long, maximum length is 256 characters" : addressError ? "Address is required" : ""}
        />
      )}

      renderOption={(props, option) => {
        const matches = option.structured_formatting?.main_text_matched_substrings || [];

        const parts = parse(
          option.structured_formatting?.main_text || '',
          matches.map((match: any) => [
            match.offset,
            match.offset + match.length,
          ])
        );

        return (
          <li {...props}>
            <Grid container alignItems="center">
              <Grid item sx={{ display: "flex", width: 44 }}>
                <LocationOnIcon sx={{ color: "text.secondary" }} />
              </Grid>
              <Grid
                item
                sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}
              >
                {parts.map((part, index) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                  >
                    {part.text}
                  </Box>
                ))}
                <Typography variant="body2" color="text.secondary">
                  {option.structured_formatting?.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
};
